import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSortable,
  SortableContext as SortableProvider,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { toast } from "react-toastify";
import { getUniqueId } from "../../constants";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/outline";
import { filterBy } from "@progress/kendo-data-query";

// Single source of truth for row arithmetic. The amount cell and the location
// total must apply the same fallbacks, otherwise a row missing price/quantity
// renders a valid amount but contributes NaN to the total.
export const getRowAmount = (row) =>
  (Number(row?.price) || 0) * (Number(row?.quantity) || 1);

export const getServicesTotal = (rows) =>
  (rows || []).reduce((sum, row) => sum + getRowAmount(row), 0);

// Sortable row component
const SortableRow = ({
  item,
  index,
  isEditing,
  editingRow,
  setEditingRow,
  filteredServices,
  onEdit,
  onSave,
  onCancel,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  isApproved,
  filterChange,
  comboBoxRef,
  unitPriceRef,
  quantityRef,
  descriptionRef,
  onAutoSaveAndNext,
  onTabNavigation,
  tableDataLength,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleServiceChange = (event) => {
    const selectedService = event.target.value;
    if (selectedService) {
      const updatedRow = {
        ...editingRow,
        serviceId: selectedService.id,
        service: selectedService.name,
        price: selectedService.price || editingRow.price || 0,
        description: selectedService.description || editingRow.description,
      };

      // Calculate amount with the new price
      updatedRow.amount = getRowAmount(updatedRow);

      setEditingRow(updatedRow);

      // Automatically move focus to unit price field after service selection
      setTimeout(() => {
        if (unitPriceRef?.current) {
          unitPriceRef.current.focus();
        }
      }, 100);
    }
  };

  const handleInputChange = (field, value) => {
    const updatedRow = {
      ...editingRow,
      [field]: value,
    };

    // Calculate amount when price or quantity changes
    if (field === "price" || field === "quantity") {
      updatedRow.amount = getRowAmount(updatedRow);
    }

    setEditingRow(updatedRow);
  };

  const handleKeyDown = (event, currentField) => {
    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();

      // Validate current row before auto-saving
      if (!editingRow?.serviceId) {
        toast.warning("Please select a service before moving to the next field.");
        return;
      }

      // Determine next field and handle navigation
      const fieldOrder = ['service', 'price', 'quantity', 'description'];
      const currentFieldIndex = fieldOrder.indexOf(currentField);
      const isLastField = currentFieldIndex === fieldOrder.length - 1;
      const isLastRow = index === tableDataLength - 1;

      if (isLastField && isLastRow) {
        // Auto-save current row and add new row
        onAutoSaveAndNext();
      } else if (isLastField) {
        // Auto-save and move to next row's first field
        onSave().then(() => {
          onTabNavigation(index + 1, 'service');
        });
      } else {
        // Move to next field in current row
        const nextField = fieldOrder[currentFieldIndex + 1];
        onTabNavigation(index, nextField);
      }
    }
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b hover:bg-gray-50 ${isDragging ? "z-50" : ""}`}
    >
      {/* Drag handle */}
      <td className="px-2 py-3 text-center">
        <div className="flex items-center justify-center space-x-1">
          {/* Drag handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab hover:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
            title="Drag to reorder"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <circle cx="3" cy="3" r="1" />
              <circle cx="9" cy="3" r="1" />
              <circle cx="3" cy="6" r="1" />
              <circle cx="9" cy="6" r="1" />
              <circle cx="3" cy="9" r="1" />
              <circle cx="9" cy="9" r="1" />
            </svg>
          </div>

          {/* Arrow buttons */}
          <div className="flex flex-col">
            <button
              onClick={onMoveUp}
              disabled={!canMoveUp || isApproved}
              className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Move up"
            >
              <ChevronUpIcon className="w-3 h-3" />
            </button>
            <button
              onClick={onMoveDown}
              disabled={!canMoveDown || isApproved}
              className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Move down"
            >
              <ChevronDownIcon className="w-3 h-3" />
            </button>
          </div>
        </div>
      </td>

      {/* Service */}
      <td className="px-4 py-3">
        {isEditing ? (
          <ComboBox
            ref={comboBoxRef}
            data={filteredServices}
            textField="name"
            dataItemKey="id"
            filterable
            onFilterChange={filterChange}
            value={editingRow.serviceId ? filteredServices.find(s => s.id === editingRow.serviceId) : null}
            onChange={handleServiceChange}
            onKeyDown={(e) => handleKeyDown(e, 'service')}
            className="w-full text-sm"
            placeholder="Select service"
          />
        ) : (
          <span className="text-sm">{item.service}</span>
        )}
      </td>

      {/* Price */}
      <td className="px-4 py-3">
        {isEditing ? (
          <NumericTextBox
            ref={unitPriceRef}
            value={editingRow.price === 0 ? null : editingRow.price}
            onChange={(e) => handleInputChange("price", e.value)}
            onKeyDown={(e) => handleKeyDown(e, 'price')}
            className="w-full text-sm"
            format="n2"
            placeholder="0"
            min={0}
          />
        ) : (
          <span className="text-sm">{parseFloat(item.price || 0).toFixed(2)}</span>
        )}
      </td>

      {/* Quantity */}
      <td className="px-4 py-3">
        {isEditing ? (
          <NumericTextBox
            ref={quantityRef}
            value={editingRow.quantity || 1}
            onChange={(e) => handleInputChange("quantity", e.value)}
            onKeyDown={(e) => handleKeyDown(e, 'quantity')}
            className="w-full text-sm"
            format="n2"
            min={0}
          />
        ) : (
          <span className="text-sm">{parseFloat(item.quantity || 1).toFixed(2)}</span>
        )}
      </td>

      {/* Amount */}
      <td className="px-4 py-3">
        <span className="text-sm font-medium">
          {isEditing
            ? getRowAmount(editingRow).toFixed(2)
            : getRowAmount(item).toFixed(2)
          }
        </span>
      </td>

      {/* Description */}
      <td className="px-4 py-3">
        {isEditing ? (
          <input
            ref={descriptionRef}
            type="text"
            value={editingRow.description || ""}
            onChange={(e) => handleInputChange("description", e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'description')}
            className="w-full p-2 text-sm border rounded"
            placeholder="Description"
          />
        ) : (
          <span className="text-sm">{item.description || ""}</span>
        )}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex space-x-2">
          {isEditing ? (
            <>
              <button
                onClick={onSave}
                className=""
              >
                <img src="save.svg" className="h-5 max-w-5" />
              </button>
              <button
                onClick={onCancel}
                className="px-2 py-1 text-xs "
              >
                <img src="closeIcon.svg" className="h-5 text-red-500" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onEdit}
                disabled={isApproved}
                className="px-2 py-1 text-xs  disabled:opacity-50"
              >
                <img
                  src="edit.svg"
                  alt=""
                  className={`w-6 `}
                />
              </button>
              <button
                onClick={onRemove}
                disabled={isApproved}
                className="px-2 py-1 text-xs  disabled:opacity-50"
              >
                <img
                  src="trash.svg"
                  alt=""
                  className={`w-6`}
                />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

const SortableServicesTable = ({ isApproved, data, services, save, locationId }) => {
  const [tableData, setTableData] = useState([]);
  const [filteredServices, setFilteredServices] = useState(services);
  const [editingRow, setEditingRow] = useState();
  const [editID, setEditID] = useState(null);

  const comboBoxRef = useRef(null);
  const unitPriceRef = useRef(null);
  const quantityRef = useRef(null);
  const descriptionRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    // Sort data by sortOrder and add index if not present
    const sortedData = [...(data || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    // Create a Set to track used indices
    const usedIndices = new Set();

    const dataWithIndex = sortedData.map((item) => {
      // If item has no index or the index is already used, generate a new unique one
      let index = item.index;
      while (!index || usedIndices.has(index)) {
        index = getUniqueId();
      }
      usedIndices.add(index);

      return {
        ...item,
        index,
        sortOrder: item.sortOrder !== undefined ? item.sortOrder : usedIndices.size - 1,
      };
    });

    setTableData(dataWithIndex);
  }, [data]);

  const filterChange = (event) => {
    const filtered = filterBy(services, event.filter);
    setFilteredServices(filtered);
  };

  const focusField = (rowIndex, fieldName) => {
    setTimeout(() => {
      const refs = {
        service: comboBoxRef,
        price: unitPriceRef,
        quantity: quantityRef,
        description: descriptionRef,
      };

      const ref = refs[fieldName];
      if (ref?.current) {
        ref.current.focus();
      }
    }, 100);
  };

  const handleTabNavigation = (rowIndex, fieldName) => {
    // Find the target row and ensure it's in edit mode
    if (rowIndex < tableData.length) {
      const targetRow = tableData[rowIndex];
      if (editID !== targetRow.index) {
        handleEdit(targetRow);
      }
      focusField(rowIndex, fieldName);
    }
  };

  const handleAutoSaveAndNext = async () => {
    if (!editingRow?.serviceId) {
      toast.warning("Please select a service before adding a new row.");
      return;
    }

    // Save the current row
    const updatedItems = tableData.map((item) =>
      item.index === editID ? { ...editingRow } : item
    );

    //     setTableData(updatedItems);
    //     save(updatedItems, locationId);
    // console.log("12");

    //     // Add a new row and start editing it
    //     const existingIndices = new Set(updatedItems.map(item => item.index));
    //     console.log(existingIndices);
  const existingIndices = new Set(updatedItems.map(item => item.index));

    let newIndex;
    do {
      newIndex = getUniqueId();
    } while (existingIndices.has(newIndex));

    const newItem = {
      index: newIndex,
      serviceId: null,
      service: "",
      description: "",
      price: 0,
      quantity: 1,
      amount: 0,
      sortOrder: updatedItems.length,
    };

    const newTableData = [...updatedItems, newItem];

    setTableData(newTableData);
    setEditID(newIndex);
    setEditingRow(newItem);

    save(newTableData, locationId);


    // Focus on the service field of the new row
    setTimeout(() => {
      if (comboBoxRef.current) {
        comboBoxRef.current.focus();
      }
    }, 100);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setTableData((items) => {
        const oldIndex = items.findIndex((item) => item.index === active.id);
        const newIndex = items.findIndex((item) => item.index === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update sortOrder for all items
        const updatedItems = newItems.map((item, index) => ({
          ...item,
          sortOrder: index,
        }));

        // Save the reordered data
        save(updatedItems, locationId);

        return updatedItems;
      });
    }
  };

  const moveItem = (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= tableData.length) return;

    const newItems = arrayMove(tableData, index, newIndex);
    const updatedItems = newItems.map((item, idx) => ({
      ...item,
      sortOrder: idx,
    }));

    setTableData(updatedItems);
    save(updatedItems, locationId);
  };

  const handleEdit = (item) => {
    setEditID(item.index);
    setEditingRow({ ...item });
  };

  const handleSave = async () => {
    if (!editingRow.serviceId) {
      toast.warning("Please select a service");
      return;
    }

    const updatedItems = tableData.map((item) =>
      item.index === editID ? { ...editingRow } : item
    );

    setTableData(updatedItems);
    setEditID(null);
    setEditingRow(null);

    save(updatedItems, locationId);
    return updatedItems;
  };

  const handleCancel = () => {
    // If it's a new row (no serviceId selected), remove it from the table
    if (editingRow && !editingRow.serviceId) {
      const updatedItems = tableData.filter((i) => i.index !== editID);
      const reIndexedItems = updatedItems.map((item, index) => ({
        ...item,
        sortOrder: index,
      }));

      setTableData(reIndexedItems);
      save(reIndexedItems, locationId);
    } else {
      // For existing rows, check if the original item had a serviceId
      const originalItem = tableData.find((item) => item.index === editID);
      if (originalItem && !originalItem.serviceId) {
        // This was an empty row that was being edited, remove it
        const updatedItems = tableData.filter((i) => i.index !== editID);
        const reIndexedItems = updatedItems.map((item, index) => ({
          ...item,
          sortOrder: index,
        }));

        setTableData(reIndexedItems);
        save(reIndexedItems, locationId);
      }
      // If it was a valid existing row, just reset the editing state without removing
    }

    setEditID(null);
    setEditingRow(null);
  };

  const handleRemove = (item) => {
    const updatedItems = tableData.filter((i) => i.index !== item.index);
    const reIndexedItems = updatedItems.map((item, index) => ({
      ...item,
      sortOrder: index,
    }));

    setTableData(reIndexedItems);
    save(reIndexedItems, locationId);
  };

  const handleAddNew = () => {
    console.log("Adding new row");

    // Get all existing indices
    const existingIndices = new Set(tableData.map(item => item.index));

    // Generate a unique index that doesn't exist in the table
    let newIndex;
    do {
      newIndex = getUniqueId();
    } while (existingIndices.has(newIndex));

    const newItem = {
      index: newIndex,
      serviceId: null,
      service: "",
      description: "",
      price: 0,
      quantity: 1,
      amount: 0,
      sortOrder: tableData.length,
    };

    const newTableData = [...tableData, newItem];

    setTableData(newTableData);
    setEditID(newIndex);
    setEditingRow(newItem);

    // Keep location.details in step with the grid — an unsaved row here would
    // be silently dropped the next time the data prop reloads tableData.
    save(newTableData, locationId);
    setTimeout(() => {
      if (comboBoxRef.current) {
        comboBoxRef.current.focus();
      }
    }, 1);
  };

  const isEditing = (item) => item.index === editID;


  return (
    <div className="w-full">

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">

              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Service
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Unit Price
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Unit
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Remarks
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {!isApproved && (
                  <div className="mb-4 flex justify-end">
                    <button
                      onClick={handleAddNew}
                      className="px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 "
                    >
                      Add Row
                    </button>
                  </div>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={tableData.filter(item => item.serviceId || isEditing(item)).map((item) => item.index)}
                strategy={verticalListSortingStrategy}
              >
                {tableData
                  .filter(item => item.serviceId || isEditing(item))
                  .map((item, index) => (
                    <SortableRow
                      key={item.index}
                      item={item}
                      index={index}
                      isEditing={isEditing(item)}
                      editingRow={editingRow}
                      setEditingRow={setEditingRow}
                      filteredServices={filteredServices}
                      onEdit={() => handleEdit(item)}
                      onSave={handleSave}
                      onCancel={handleCancel}
                      onRemove={() => handleRemove(item)}
                      onMoveUp={() => moveItem(index, "up")}
                      onMoveDown={() => moveItem(index, "down")}
                      canMoveUp={index > 0}
                      canMoveDown={index < tableData.length - 1}
                      isApproved={isApproved}
                      filterChange={filterChange}
                      comboBoxRef={comboBoxRef}
                      unitPriceRef={unitPriceRef}
                      quantityRef={quantityRef}
                      descriptionRef={descriptionRef}
                      onAutoSaveAndNext={handleAutoSaveAndNext}
                      onTabNavigation={handleTabNavigation}
                      tableDataLength={tableData.length}
                    />
                  ))}
              </SortableContext>
            </DndContext>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SortableServicesTable;
